import "@cfg/configurator-ui/skin.css";
import "./styles.css";
import { createApiClient } from "@cfg/api-contract";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";
import { Analytics } from "./analytics.ts";
import { App } from "./app.tsx";
import { AppCtx, type AppServices } from "./app-context.tsx";
import { Auth } from "./auth.ts";
import { makeCopy, resolveBrand } from "./brand.ts";
import { ApiRegistry, LocalRegistry } from "./registry.ts";

async function boot() {
  const brand = await resolveBrand();
  const auth = new Auth(brand.config);
  await auth.init();

  const apiBase = brand.config.apiBaseUrl;
  const api = apiBase ? createApiClient({ baseUrl: apiBase, brand: brand.config.id, getToken: () => auth.getToken() }) : null;
  const registryUrl = import.meta.env.VITE_REGISTRY_URL;
  const registry = api && !registryUrl ? new ApiRegistry(api, brand.config) : new LocalRegistry(registryUrl ?? "/packs/registry.json", brand.config);

  const services: AppServices = {
    brand,
    copy: makeCopy(brand.config),
    registry,
    api,
    auth,
    analytics: new Analytics(api, brand.config.features.analytics, apiBase),
  };
  const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 60_000, retry: 1 } } });

  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <AppCtx.Provider value={services}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </AppCtx.Provider>
    </StrictMode>,
  );
}

boot().catch((err) => {
  console.error(err);
  document.getElementById("root")!.innerHTML = `<pre style="padding:2rem;color:#c0392b">Failed to start: ${String(err)}</pre>`;
});
